<template>
    <div class="webform">
        <div class="sr-only" role="status">
            <div v-if="showSuccess" v-html="submission.success"></div>
        </div>
        <div v-if="!showForm && submission?.success" class="text-black p-3 bg-white rounded-lg">
            <div v-html="submission.success"></div>
            <div class="mt-6">
                <button @click="showForm = true" type="button" class="appearance-none text-base hover:underline text-orange-900">
                    Go back to the form
                </button>
            </div>
        </div>

        <template v-else>
            <div class="space-y-10">
                <div v-if="content?.length" class="bg-white rounded-lg p-4 sm:p-6 lg:p-8">
                    <SetsRenderer :sets="content" />
                </div>

                <div v-show="errors?.length || (captcha.enabled && captcha?.errors?.length)" ref="errorSummary" tabindex="-1" class="bg-white rounded-lg p-4 sm:p-6 lg:p-8 scroll-mt-24 focus:outline-none">
                    <div>
                        <h2 class="text-lg font-bold text-gray-700">We found some errors</h2>
                        <p class="text-sm text-gray-500 mt-2">Please fix the following errors and try again.</p>
                    </div>
                    <ul v-if="errors?.length" class="rounded-lg text-sm text-red-500">
                        <li v-for="error in errors" :key="error">{{ error }}</li>
                    </ul>
                </div>

                <div class="bg-white rounded-lg p-0 sm:p-4 lg:p-8">
                    <form v-if="showForm" :action="action" method="POST" enctype="multipart/form-data" class="max-w-3xl mx-auto p-2 md:p-10 lg:p-16" @keydown.enter.prevent @keyup.enter.prevent @submit="onSubmit">
                        <input type="hidden" name="_token" :value="csrfToken">
                        <div class="flex flex-wrap">
                            <!-- Fallback to show required help text when first field is not a section -->
                            <div v-if="fields && fields[0].type !== 'section'" class="w-full bg-gray-100 text-gray-800 text-base font-normal p-4 my-6 rounded">
                                Fields with <strong>*</strong> are required.
                            </div>
                            <!-- Render form fields -->
                            <template v-for="field in fields">
                                <div v-if="isFieldVisible(field)" class="p-2" :class="[getWidthClass(field)]">
                                    <div>
                                        <component :is="getComponent(field)" :field="field" :id="`form_field_${field.handle}`" @change="value => onFieldChange(field, value)" />
                                    </div>
                                    <!-- Show error here -->
                                    <p v-if="field.errors?.length" class="text-sm text-red-500 mt-1">
                                        <span v-html="field.errors.join('<br/>')"></span>
                                    </p>
                                </div>
                            </template>
                        </div>

                        <input v-if="honeypot" :name="honeypot" type="text" class="hidden">

                        <div class="flex justify-end px-3 py-2">
                            <button class="py-2 px-4 bg-primary text-white rounded" type="submit">
                                Submit
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </template>
    </div>
</template>

<script>
import {reactive, ref, defineComponent, watch, onMounted} from 'vue';
import BusStopAmenities from "../../pages/QOL/Page2/TransitStopFeatures";
import SetsRenderer from "../SetsRenderer";
import Assets from "./Fields/Assets";
import Default from "./Fields/Default";
import Checkboxes from "./Fields/Checkboxes";
import Radio from "./Fields/Radio";
import Date from "./Fields/Date";
import Time from "./Fields/Time";
import Section from "./Fields/Section";
import Select from "./Fields/Select";
import DynamicSelect from "./Fields/DynamicSelect.vue";
import Textarea from "./Fields/Textarea";
import Telephone from "./Fields/Telephone";
import MaterialOrdering from "./Fields/MaterialOrdering";
import RadioAccessible from "./Fields/RadioAccessible.vue";
import {useScriptTag} from '@vueuse/core';
import {usePage} from "@inertiajs/vue3";
import {getRecaptchaFields} from "../../utils/recaptcha";

export default defineComponent({
    name: 'Webform',
    components: {BusStopAmenities, SetsRenderer},
    props: {
        action: {
            required: true,
            type: String,
        },
        handle: {
            required: true,
            type: String,
        },
        title: {
            type: [String, null],
            default: null,
        },
        content: {
            type: [Array, null],
            default: null,
        },
        fields: {
            required: true,
            type: Array,
        },
        honeypot: {
            type: [String, null],
            default: null,
        },
        captcha: {
            type: Object,
            required: true,
        },
        csrfToken: {
            type: String,
            required: true,
        },
        submission: {
            type: [Object, null],
            required: true,
        },
        errors: {
            type: [Array, null],
            required: true,
        },
    },

    setup(props) {
        const showForm = ref(! props.submission?.success);

        const showSuccess = ref(false);

        const errorSummary = ref(null);

        const submitting = ref(false);

        if (props.captcha.enabled) {
            window.recaptchaV3 = {};
            window.recaptchaV3.siteKey = props.captcha.siteKey;
            window.recaptchaV3.action = props.handle.replace('-', '_');
            window.recaptchaV3.verifyOnPageLoad = true;

            useScriptTag('/vendor/statamic-recaptcha/js/recaptcha-v3.js');
        }

        const getComponent = (field) => {
            if (field.type === 'text' && field.input_type === 'tel') {
                return Telephone;
            }

            switch (field.type) {
                case 'assets': return Assets;
                case 'checkboxes': return Checkboxes;
                case 'date': return Date;
                case 'radio': return usePage().props.global.feature_flags.enable_accessible_radio ? RadioAccessible : Radio;
                case 'section': return Section;
                case 'select': return Select;
                case 'dynamic_select': return DynamicSelect;
                case 'textarea': return Textarea;
                case 'time': return Time;
                case 'material_ordering': return MaterialOrdering;
                default: return Default;
            }
        };

        const getWidthClass = ((field) => {
            if (field?.width || parseInt(field.width) === 100) {
                return 'w-full';
            }

            const width = (field.width / 100) * 12;
            return `w-${width}/12`;
        });

        // Tracks each field's current value by handle, seeded from any prior
        // submission (`old`) so a conditional field redisplayed with errors
        // stays visible/hidden consistently with what was submitted.
        const fieldValues = reactive(
            Object.fromEntries(props.fields.map((field) => [field.handle, field.old ?? null]))
        );

        const onFieldChange = (field, value) => {
            fieldValues[field.handle] = value;
        }

        const isFieldVisible = (field) => {
            if (field.if && Object.keys(field.if).length) {
                return Object.entries(field.if).every(([handle, expected]) => fieldValues[handle] === expected);
            }

            if (field.unless && Object.keys(field.unless).length) {
                return Object.entries(field.unless).every(([handle, expected]) => fieldValues[handle] !== expected);
            }

            return true;
        }

        // Replace the page-load captcha token with a fresh one, since tokens
        // expire after two minutes and most forms take longer than that.
        const onSubmit = async (event) => {
            if (! props.captcha.enabled) {
                return;
            }

            event.preventDefault();

            if (submitting.value) {
                return;
            }

            submitting.value = true;

            const form = event.target;
            const fields = await getRecaptchaFields();

            if (fields) {
                Object.entries(fields).forEach(([name, value]) => {
                    form.querySelectorAll(`input[name="${name}"]`).forEach((input) => input.remove());

                    const input = document.createElement('input');
                    input.type = 'hidden';
                    input.name = name;
                    input.value = value;
                    form.appendChild(input);
                });
            }

            form.submit();
        };

        // Validation errors render in a summary above the form, so bring it into view.
        onMounted(() => {
            if (props.errors?.length && errorSummary.value) {
                errorSummary.value.scrollIntoView({behavior: 'smooth', block: 'start'});
                errorSummary.value.focus({preventScroll: true});
            }
        });

        watch(() => {
            return !showForm.value && props.submission?.success
        }, (status) => {
            window.setTimeout(() => showSuccess.value = status, 500)
        }, { immediate: true })

        return {
            showForm,
            showSuccess,
            getComponent,
            getWidthClass,
            onFieldChange,
            isFieldVisible,
            onSubmit,
            errorSummary,
        }
    },
});
</script>
